import { useEffect, useState } from "react";
import { useAuth, useReverification, useUser } from "@clerk/react";
import type { ExternalAccountResource, OAuthStrategy } from "@clerk/shared/types";
import {
  LinkIcon,
  MoreVerticalIcon,
  PlusIcon,
  RefreshCwIcon,
  Tag,
  UnlinkIcon,
  UnplugIcon,
} from "lucide-react";
import { toast } from "sonner";
import { useUserSettingsStore } from "@/stores";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import EmptyState from "@/components/ui/empty-state";
import {
  Frame,
  FrameHeader,
  FrameTitle,
  FrameDescription,
  FramePanel,
  FrameAction,
  FrameCount,
} from "@/components/ui/frame";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toastClerkError } from "@/lib/clerk-errors";
import google from "@/assets/google.svg";
import microsoft from "@/assets/microsoft.svg";
import tiktok from "@/assets/tiktok.svg";
import dub_logo_dark from "@/assets/dub_logo_dark.svg";
import dub_logo_light from "@/assets/dub_logo_light.svg";

// What a campaign mailbox needs: the same scopes MailPermissionGuard asks for.
const MAIL_SCOPES: Record<string, string[]> = {
  google: [
    "https://www.googleapis.com/auth/gmail.send",
    "https://www.googleapis.com/auth/gmail.readonly",
  ],
  microsoft: [
    "https://graph.microsoft.com/User.Read",
    "https://graph.microsoft.com/Mail.Read",
    "https://graph.microsoft.com/Mail.Send",
    "https://graph.microsoft.com/Mail.ReadWrite",
  ],
};
// approvedScopes fragments that mean the mailbox can send (MailPermissionGuard's check).
const MAIL_SCOPE_CHECKS: Record<string, string[]> = {
  google: ["gmail.send", "gmail.readonly"],
  microsoft: ["Mail.Read", "Mail.Send", "Mail.ReadWrite"],
};
// Labeling Amy's threads needs this one on top of the mail scopes.
const GMAIL_MODIFY_SCOPE = "https://www.googleapis.com/auth/gmail.modify";
// Sign-in providers enabled on both Clerk instances. Accounts on other
// providers (TikTok on dev) still list, they just can't be added here.
const CONNECTABLE_PROVIDERS: {
  provider: string;
  strategy: OAuthStrategy;
  label: string;
}[] = [
  { provider: "google", strategy: "oauth_google", label: "Google" },
  { provider: "microsoft", strategy: "oauth_microsoft", label: "Microsoft" },
  { provider: "custom_dub", strategy: "oauth_custom_dub", label: "Dub" },
];
const PROVIDER_ICONS: Record<string, string> = { google, microsoft, tiktok };

/** The frontend reports Dub as "custom_dub"; the backend API as "oauth_custom_dub". */
function providerKey(account: ExternalAccountResource) {
  return account.provider.replace(/^oauth_/, "");
}

function providerLabel(account: ExternalAccountResource) {
  return (
    CONNECTABLE_PROVIDERS.find((p) => p.provider === providerKey(account))?.label ??
    account.providerTitle()
  );
}

function hasMailAccess(account: ExternalAccountResource) {
  const checks = MAIL_SCOPE_CHECKS[providerKey(account)];
  return !checks || checks.every((scope) => account.approvedScopes.includes(scope));
}

/**
 * Scopes for a reconnect: the mail scopes plus every URL scope already
 * granted, so reconnecting never drops one (e.g. gmail.modify for labeling).
 */
function reconnectScopes(account: ExternalAccountResource) {
  const granted = account.approvedScopes
    .split(/\s+/)
    .filter((scope) => scope.startsWith("https://"));
  const scopes = [...new Set([...(MAIL_SCOPES[providerKey(account)] ?? []), ...granted])];
  return scopes.length ? scopes : undefined;
}

function oauthRedirectUrl() {
  return `${window.location.origin}${window.location.pathname}`;
}

function ProviderIcon({ provider }: { provider: string }) {
  if (provider === "custom_dub") {
    return (
      <>
        <img src={dub_logo_light} alt="" className="size-3.5 dark:hidden" />
        <img src={dub_logo_dark} alt="" className="hidden size-3.5 dark:block" />
      </>
    );
  }
  const src = PROVIDER_ICONS[provider];
  return src ? (
    <img src={src} alt="" className="size-3.5" />
  ) : (
    <LinkIcon className="size-3.5 text-muted-foreground" />
  );
}

/**
 * OAuth accounts on your Clerk user: the Gmail / Outlook mailbox that
 * campaigns not run by Amy send from (the Google row's menu also holds Gmail
 * labeling), and the Dub account behind affiliate links. Connecting
 * redirects to the provider and lands back here.
 */
export function ConnectedAccountsFrame() {
  const { user } = useUser();
  const [connecting, setConnecting] = useState<string | null>(null);
  const connect = useReverification((strategy: OAuthStrategy, provider: string) => {
    if (!user) throw new Error("not_logged_in");
    return user.createExternalAccount({
      strategy,
      redirectUrl: oauthRedirectUrl(),
      additionalScopes: MAIL_SCOPES[provider],
      oidcPrompt: MAIL_SCOPES[provider] ? "consent" : undefined,
    });
  });

  if (!user) return null;

  const accounts = user.externalAccounts;
  const connected = new Set(accounts.map(providerKey));
  const available = CONNECTABLE_PROVIDERS.filter((p) => !connected.has(p.provider));

  const handleConnect = async (p: (typeof CONNECTABLE_PROVIDERS)[number]) => {
    setConnecting(p.provider);
    try {
      const account = await connect(p.strategy, p.provider);
      const url = account?.verification?.externalVerificationRedirectURL;
      if (url) {
        window.location.href = url.href;
        return;
      }
      setConnecting(null);
    } catch (error) {
      toastClerkError(error, `Failed to connect ${p.label}`);
      setConnecting(null);
    }
  };

  return (
    <Frame>
      <FrameHeader>
        <FrameTitle>
          Connected accounts <FrameCount>{accounts.length}</FrameCount>
        </FrameTitle>
        <FrameDescription>
          Your mailbox for campaigns Amy doesn't run, and Dub for affiliate links.
        </FrameDescription>
        <FrameAction>
          {available.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" className="shrink-0" disabled={connecting !== null}>
                  {connecting ? <Spinner /> : <PlusIcon />}
                  Connect
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {available.map((p) => (
                  <DropdownMenuItem key={p.provider} onSelect={() => void handleConnect(p)}>
                    <ProviderIcon provider={p.provider} />
                    {p.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </FrameAction>
      </FrameHeader>

      {accounts.length > 0 ? (
        <Table variant="card">
          <TableHeader>
            <TableRow>
              <TableHead>Account</TableHead>
              <TableHead className="w-40">Status</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {accounts.map((account) => (
              <ConnectedAccountRow key={account.id} account={account} />
            ))}
          </TableBody>
        </Table>
      ) : (
        <FramePanel>
          <EmptyState
            className="mb-10"
            icon={<UnplugIcon />}
            text="Nothing connected yet"
          />
        </FramePanel>
      )}
    </Frame>
  );
}

function ConnectedAccountRow({ account }: { account: ExternalAccountResource }) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState<"reconnect" | "disconnect" | null>(null);
  const reauthorize = useReverification(() =>
    account.reauthorize({
      redirectUrl: oauthRedirectUrl(),
      additionalScopes: reconnectScopes(account),
      oidcPrompt: "consent",
    }),
  );
  const disconnect = useReverification(() => account.destroy());

  const provider = providerKey(account);
  const label = providerLabel(account);
  const identifier = account.emailAddress || account.username || account.accountIdentifier();
  const isMailbox = provider in MAIL_SCOPES;
  const verified = account.verification?.status === "verified";
  const status = !verified
    ? { label: "Reconnect needed", variant: "warning" as const }
    : isMailbox && !hasMailAccess(account)
      ? { label: "No mail access", variant: "warning" as const }
      : { label: "Connected", variant: "success" as const };

  const handleReconnect = async () => {
    setBusy("reconnect");
    try {
      const res = await reauthorize();
      const url = res?.verification?.externalVerificationRedirectURL;
      if (url) {
        window.location.href = url.href;
        return;
      }
      setBusy(null);
    } catch (error) {
      toastClerkError(error, `Failed to reconnect ${label}`);
      setBusy(null);
    }
  };

  const handleDisconnect = async () => {
    setBusy("disconnect");
    try {
      await disconnect();
      toast.success(`${label} disconnected`);
    } catch (error) {
      toastClerkError(error, `Failed to disconnect ${label}`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <TableRow data-context-menu>
      <TableCell>
        <div className="flex min-w-0 items-center gap-2">
          <div className="flex size-7 shrink-0 items-center justify-center rounded-md bg-muted">
            <ProviderIcon provider={provider} />
          </div>
          <div className="grid min-w-0 leading-tight">
            <span className="truncate text-sm font-medium">{label}</span>
            {identifier && (
              <span className="truncate text-xs text-muted-foreground">{identifier}</span>
            )}
          </div>
        </div>
      </TableCell>
      <TableCell>
        <Badge variant={status.variant}>{status.label}</Badge>
      </TableCell>
      <TableCell>
        <div className="flex justify-end">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="muted-ghost"
                size="icon-sm"
                aria-label="Account options"
                disabled={busy !== null}
              >
                {busy ? <Spinner className="size-3.5" /> : <MoreVerticalIcon />}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onSelect={() => void handleReconnect()}>
                <RefreshCwIcon />
                {status.variant === "success" ? "Reconnect" : "Fix connection"}
              </DropdownMenuItem>
              {provider === "google" && <GmailLabelingItem account={account} />}
              <DropdownMenuSeparator />
              <DropdownMenuItem variant="destructive" onSelect={() => setConfirmOpen(true)}>
                <UnlinkIcon />
                Disconnect
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
            <AlertDialogContent className="max-w-md">
              <AlertDialogHeader>
                <AlertDialogTitle>Disconnect {label}?</AlertDialogTitle>
                <AlertDialogDescription>
                  {isMailbox
                    ? "Campaigns not run by Amy that send from this mailbox stop sending until you connect it again."
                    : provider === "custom_dub"
                      ? "If you're the organization's Dub user, new affiliate links can't be created until a Dub account is connected again."
                      : "You can connect it again any time."}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={() => void handleDisconnect()} variant="destructive">
                  <UnlinkIcon />
                  Disconnect
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </TableCell>
    </TableRow>
  );
}

/**
 * The Google row's menu item for the [AMY_AGENT] Gmail label
 * (users.enable_gmail_labeling, always the logged-in user): a checkbox once
 * gmail.modify is granted, until then the item that asks Google for it.
 */
function GmailLabelingItem({ account }: { account: ExternalAccountResource }) {
  const { userInfo, saveUserSettingFor } = useUserSettingsStore();
  const { orgId, getToken } = useAuth();
  const { user } = useUser();
  const [enabled, setEnabled] = useState(true);

  useEffect(() => {
    setEnabled(userInfo?.enable_gmail_labeling ?? true);
  }, [userInfo]);

  const granted = account.approvedScopes?.includes(GMAIL_MODIFY_SCOPE);

  const handleToggle = async () => {
    if (!orgId || !user?.id) return;
    const newValue = !enabled;
    setEnabled(newValue);

    const token = await getToken();
    if (!token) return;

    const success = await saveUserSettingFor(
      user.id,
      orgId,
      "enable_gmail_labeling",
      newValue,
      token,
      { mirrorToGlobal: true },
    );

    if (success) {
      toast.success("Gmail labeling preference saved");
    } else {
      toast.error("Failed to save Gmail labeling preference");
      setEnabled(!newValue);
    }
  };

  const handleGrant = () => {
    toast.promise(
      (async () => {
        const response = await account.reauthorize({
          redirectUrl: oauthRedirectUrl(),
          additionalScopes: [GMAIL_MODIFY_SCOPE],
          oidcPrompt: "consent",
        });
        if (response?.verification?.externalVerificationRedirectURL) {
          window.location.href = response.verification.externalVerificationRedirectURL.href;
        }
      })(),
      {
        loading: "Requesting Gmail permission...",
        success: "Redirecting to Google...",
        error: (error: { errors?: { long_message?: string; message?: string }[] }) => {
          console.error("Error requesting Gmail permission:", error);
          return (
            error?.errors?.[0]?.long_message ||
            error?.errors?.[0]?.message ||
            "Failed to request Gmail permission. Please try again."
          );
        },
      },
    );
  };

  return granted ? (
    <DropdownMenuCheckboxItem checked={enabled} onCheckedChange={() => void handleToggle()}>
      Label Amy's threads in Gmail
    </DropdownMenuCheckboxItem>
  ) : (
    <DropdownMenuItem onSelect={handleGrant}>
      <Tag />
      Allow Gmail labeling
    </DropdownMenuItem>
  );
}
